import { useMutation, useQuery } from '@tanstack/react-query';
import type { BisBuildArea, BisRefStatus } from '@raro/domain';
import { Button, cx, Label, Notice, Serif } from '@raro/ui';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { ReferenceRoom } from './ReferenceRoom';

type Area = { key: BisBuildArea; label: string; tab: string | null; feeds: string; empty: string; has: boolean; parts: { label: string; basis: string; lines: number }[] };
type Construction = { id: string | null; status: BisRefStatus | null; changed: boolean; readAt: string | null; areas: Area[] };

/**
 * A construção do BIS: a Machine lê o que as áreas do cliente já sabem (marca, produtos e serviços, escuta, futuro, mercado,
 * praças, dados externos, o que a Raro já decidiu), capítulo por capítulo, e devolve achados já como bloco, com a área de
 * onde vieram. Onde duas áreas não dizem a mesma coisa, mostra os dois lados e pergunta: nunca escolhe. À esquerda, o
 * estado de cada área, por regra, com o que falta; no meio, os achados; à direita, a conversa. A sala é a mesma dos
 * documentos de referência, com o material das áreas no lugar de um arquivo.
 */
export function ConstructionRoom({ organizationId, brandId, canEdit, onBis }: { organizationId: string; brandId?: string; canEdit: boolean; onBis: () => void }) {
  const qs = brandId ? `?brandId=${brandId}` : '';
  const q = useQuery({ queryKey: ['bis-construction', organizationId, qs], queryFn: () => api.get<Construction>(`/organizations/${organizationId}/bis/construction${qs}`) });
  const start = useMutation({ mutationFn: () => api.post<{ id: string }>(`/organizations/${organizationId}/bis/construction${qs}`), onSuccess: () => void q.refetch() });
  const c = q.data;
  const client = (tab: string | null) => (tab ? `/clientes/${organizationId}?aba=${encodeURIComponent(tab)}${brandId ? `&marca=${brandId}` : ''}` : null);

  return (
    <div className="rr-bisbuild">
      <aside className="rr-bisbuild__nav">
        <Label>as áreas</Label>
        {!c && <p className="rr-faint">{q.isError ? (q.error as Error).message : 'carregando…'}</p>}
        {c?.areas.map((a) => {
          const href = client(a.tab);
          return (
            <div key={a.key} className={cx('rr-bisbuild__area', !a.has && 'is-empty')}>
              <b>{href ? <Link to={href}>{a.label}</Link> : a.label}</b>
              {a.has ? a.parts.map((p) => <small key={p.label}>{p.basis}</small>) : <small className="rr-faint">{a.empty}</small>}
            </div>
          );
        })}
        {c?.id && (
          <div className="rr-bisbuild__box">
            {c.changed ? <p>entrou material novo nas áreas desde a última leitura{c.readAt ? ` (${formatDate(c.readAt)})` : ''}.</p> : <small className="rr-faint">a Machine leu o material como ele está hoje.</small>}
            {canEdit && c.changed && <Button size="sm" variant="primary" disabled={start.isPending || c.status === 'thinking'} onClick={() => start.mutate()}>pedir que a Machine releia</Button>}
            {start.isError && <small>{(start.error as Error).message}</small>}
          </div>
        )}
      </aside>

      {c?.id ? <ReferenceRoom key={c.id} build organizationId={organizationId} id={c.id} canEdit={canEdit} onBis={() => { onBis(); void q.refetch(); }} /> : (
        <main className="rr-bisbuild__list">
          <header><div><h2>construção</h2><p>a Machine lê o que cada área do cliente já sabe e propõe o BIS capítulo por capítulo, com a área de onde veio cada coisa.</p></div></header>
          {c && (
            <div className="rr-stack rr-stack--3">
              <p><Serif italic>{c.areas.filter((a) => a.has).length} de {c.areas.length} áreas têm material.</Serif> onde duas áreas não dizem a mesma coisa, ela mostra os dois lados e pergunta. nada entra no BIS sem você aceitar.</p>
              {c.areas.some((a) => !a.has) && <Notice>sem material ainda: {c.areas.filter((a) => !a.has).map((a) => a.label).join(', ')}. dá para começar assim e pedir uma nova leitura quando essas áreas tiverem material.</Notice>}
              {canEdit ? <div><Button variant="primary" disabled={start.isPending} onClick={() => start.mutate()}>{start.isPending ? 'abrindo…' : 'começar a construção'}</Button></div> : <small className="rr-faint">abrir a construção é de quem edita o BIS.</small>}
              {start.isError && <Notice tone="error">{(start.error as Error).message}</Notice>}
            </div>
          )}
        </main>
      )}
    </div>
  );
}
